import { useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import type { ApplicationStage, JobDetail, PipelineConfig } from '@nest-recruit/shared';
import { Card } from '../../../components/Card';
import { jobKey, useSaveJobStep } from '../api/jobs-api';
import { useStepAutosave } from '../hooks/use-step-autosave';
import { StageBuilder } from './StageBuilder';
import type { StepProps } from './step-props';
import styles from './steps.module.css';

/** JOB-05 — step 4: this job's copy of the pipeline (F5.07). */

/** C4 (ADR 0077): the test and the rounds are switched by the two counters above; here they only show it. */
const SET_BY_COUNTERS = 'Set by the counters above';
const FOLLOWED_STAGES: Readonly<Partial<Record<ApplicationStage, string>>> = {
  assessment: SET_BY_COUNTERS,
  interview_1: SET_BY_COUNTERS,
  interview_2: SET_BY_COUNTERS,
  interview_3: SET_BY_COUNTERS,
};

function draftOf(job: JobDetail): PipelineConfig {
  return {
    stages: job.pipeline.stages.map(({ isLocked: _isLocked, ...stage }) => stage),
    outcomes: job.pipeline.outcomes.map((outcome) => ({ ...outcome })),
  };
}

/**
 * The counted stages as the job has them now. The counters save them; this draft may be older, and
 * sending its old ticks would switch them back (the server keeps the job's own either way).
 */
export function withCountedStages(pipeline: PipelineConfig, current: Pick<JobDetail, 'pipeline'>): PipelineConfig {
  const isOn = (stage: ApplicationStage): boolean | undefined => current.pipeline.stages.find((row) => row.stage === stage)?.isActive;
  return {
    ...pipeline,
    stages: pipeline.stages.map((stage) => (stage.stage in FOLLOWED_STAGES ? { ...stage, isActive: isOn(stage.stage) ?? stage.isActive } : stage)),
  };
}

export function PipelineStep({ job, isReadOnly, onConflict, footer }: StepProps): ReactNode {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<PipelineConfig>(() => draftOf(job));
  const saveStep = useSaveJobStep();
  const { state, schedule, flush } = useStepAutosave<PipelineConfig>({
    job,
    onConflict,
    // Read when the save's turn comes, so a test switched on just before is never undone.
    save: (pipeline, version) => {
      const current = queryClient.getQueryData<JobDetail>(jobKey(job.id)) ?? job;
      return saveStep.mutateAsync({ id: job.id, step: 'pipeline', body: { version, pipeline: withCountedStages(pipeline, current) } });
    },
  });
  const locked = new Set(job.pipeline.stages.filter((stage) => stage.isLocked).map((stage) => stage.stage));

  return (
    <div className={styles.step}>
      <Card
        title="Pipeline"
        meta="Stages run in this order. Switch the optional ones on or off, and name each one for your team and for candidates"
      >
        {job.hasCandidates && (
          <p className={styles.notice}>
            Candidates have reached the marked stages. Changing one of those keeps the current setup as
            version {job.currentConfigVersion} and moves this job to version {job.currentConfigVersion + 1}.
          </p>
        )}
        <StageBuilder
          value={withCountedStages(draft, job)}
          lockedStages={locked}
          isReadOnly={isReadOnly}
          followedStages={FOLLOWED_STAGES}
          onChange={(next) => {
            setDraft(next);
            schedule(next);
          }}
        />
      </Card>

      {footer({ state, flush })}
    </div>
  );
}
